// The page part of the top bar's breadcrumbs (spec/dashboard.md section 3.3): the URL segments after the branch, the
// environment or the game (section 3.1) read as page titles, not as path pieces. `liveops/offers` is "Offers",
// `templates/<name>/documents` is "Documents › <template display name>", `history/<sha>` is "History › <sha8>". The
// organization, game, branch and environment crumbs before them are GameLayout's. Pure: the display names come in
// through `ctx` (catalogLabels reads them from the query cache).
import type { EnumDefinition, Template } from '../api/types';
import { queryCache } from '../workspace/cache';
import { controlItems, liveOpsItems, routes } from './routes';
import type { Crumb } from './TopBar';

export interface CrumbContext {
  org: string;
  /** the game's slug */
  game: string;
  branch: string | null;
  env: string | null;
  /** a template's display name by its full name; undefined falls back to the short name */
  templateLabel?: (name: string) => string | null | undefined;
  /** an enum's display name by its name; undefined falls back to the short name */
  enumLabel?: (name: string) => string | null | undefined;
}

const labelOf = (key: string, fallback: string) => controlItems.find((i) => i.key === key)?.label ?? fallback;
const shortName = (name: string) => name.split('.').pop() || name;
/** a tab's title from its URL key: `addressables` → "Addressables", `store-items` → "Store items" */
const tabTitle = (key: string) => {
  const s = key.replace(/-/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const decode = (s: string) => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** The crumbs of the page segment (the path after `branches/{b}/`, `envs/{e}/` or `games/{g}/`); the last is the current page. */
export function crumbsFor(pageSegment: string, ctx: CrumbContext): Crumb[] {
  const parts = (pageSegment.split('?')[0] ?? '').split('/').filter(Boolean).map(decode);
  if (parts.length === 0) return [];
  const [first, second, third] = parts as [string, string | undefined, string | undefined];
  const { org, game, branch, env } = ctx;
  const tpl = (name: string) => ctx.templateLabel?.(name) || shortName(name);
  const enm = (name: string) => ctx.enumLabel?.(name) || shortName(name);

  if (env) {
    const at = (page: string) => routes.env(org, game, env, page);
    if (first === 'players') {
      const players: Crumb = { label: labelOf('players', 'Players'), to: at('players') };
      if (second === 'erasures' && third) return [players, { label: 'Erasure receipt' }];
      if (second) return [players, { label: second.slice(0, 8) }];
      return [{ label: players.label }];
    }
    if (first === 'orders') return [{ label: labelOf('orders', 'Orders') }];
    if (first === 'platforms') return [{ label: labelOf('platforms', 'Platforms') }];
    if (first === 'settings') return [{ label: 'Environment settings' }];
    return parts.map((p) => ({ label: p }));
  }

  if (!branch) {
    if (first === 'settings') {
      const settings = labelOf('settings', 'Settings');
      return second ? [{ label: settings, to: routes.settings(org, game) }, { label: tabTitle(second) }] : [{ label: settings }];
    }
    return parts.map((p) => ({ label: p }));
  }

  const at = (page: string) => routes.branch(org, game, branch, page);
  /** a page with an optional sub-page: "Title › Sub" with the title linking to the page, or just "Title" */
  const withSub = (title: string, to: string, sub: string | undefined): Crumb[] => (sub ? [{ label: title, to }, { label: sub }] : [{ label: title }]);
  switch (first) {
    case 'liveops': {
      const item = liveOpsItems.find((i) => i.page === `liveops/${second ?? ''}`);
      return [{ label: item?.label ?? (second ? tabTitle(second) : 'LiveOps') }];
    }
    case 'templates': {
      const structure = labelOf('templates', 'Data structure');
      if (!second) return [{ label: structure }];
      if (second === 'new') return [{ label: structure, to: routes.templates(org, game, branch) }, { label: 'New template' }];
      // the Documents template tree lives in the Content panel: it has no page of its own to link to
      if (third === 'documents') return [{ label: 'Documents' }, { label: tpl(second) }];
      return [{ label: structure, to: routes.templates(org, game, branch) }, { label: tpl(second) }];
    }
    case 'enums':
      if (second === 'new') return withSub('Enums', routes.enums(org, game, branch), 'New enum');
      return withSub('Enums', routes.enums(org, game, branch), second ? enm(second) : undefined);
    case 'profiles':
      return [{ label: 'Profiles' }];
    case 'tags':
      return [{ label: 'Tags' }];
    case 'search':
      return [{ label: 'Search' }];
    case 'localization':
      return withSub(labelOf('localization', 'Localization'), at('localization/strings'), second ? tabTitle(second) : undefined);
    case 'assets':
      return withSub(labelOf('assets', 'Assets'), at('assets/files'), second ? tabTitle(second) : undefined);
    case 'products':
      return [{ label: labelOf('products', 'Products') }];
    case 'extensions':
      return withSub(labelOf('extensions', 'Extensions'), at('extensions'), second);
    case 'history':
      return withSub(labelOf('history', 'History'), routes.history(org, game, branch), second ? second.slice(0, 8) : undefined);
    case 'merge-requests':
      return withSub(labelOf('merge-requests', 'Merge requests'), routes.mergeRequests(org, game, branch), second ? `!${second}` : undefined);
    case 'releases':
      return withSub(labelOf('releases', 'Releases'), routes.releases(org, game, branch), second ? tabTitle(second) : undefined);
    default:
      return parts.map((p) => ({ label: p }));
  }
}

/**
 * The display-name lookups of a branch from the catalog the pages already loaded into the query cache (the keys of
 * content/ContentContext.tsx, as the palette's search reads them); before it is there, the crumbs show short names.
 */
export function catalogLabels(gameId: string, branch: string | null): Pick<CrumbContext, 'templateLabel' | 'enumLabel'> {
  if (!branch) return {};
  const key = (suffix: string) => `branch:${gameId}:${branch}:${suffix}`;
  const templates = queryCache.peek<Template[]>(key('templates:all')) ?? queryCache.peek<Template[]>(key('templates'));
  const enums = queryCache.peek<EnumDefinition[]>(key('enums'));
  return {
    templateLabel: (name) => templates?.find((t) => t.name === name)?.displayName,
    enumLabel: (name) => enums?.find((e) => e.name === name)?.displayName,
  };
}
